/**
 * The screen catalog — one source of truth for every route in the app.
 *
 * Spec section 7 lists the web screens; `FND-11` requires every one of them to
 * have a reachable placeholder route. Keeping that list in the router, in the
 * navigation, and in a test would mean three lists that agree until they do not,
 * so there is one list and the router, the navigation and the tests all read it.
 *
 * ── Count ────────────────────────────────────────────────────────────────
 *
 * Section 7's table has **23** screen names, not the 20 the design review cites.
 * Both numbers appear in the plan and neither is derivable from the other; the
 * catalog implements all 23 and `catalog.spec.ts` pins the number, so the
 * next person to change it has to mean it. Two further screens are included that
 * section 7 omits but the rest of the plan demands:
 *
 *   - **`org-approval`** — `ORG-05`'s approval wait. DESIGN.md section 11 designs
 *     it in detail and calls it "the first thing your first customer sees", and
 *     ADR 0004 makes the facility user #1. Section 7 has no row for it.
 *   - **`staff-audit`** — `ORG-09`'s audit log view. Spec section 6 has
 *     `GET /admin/audit`, DESIGN.md section 6 lists "audit log view" among the
 *     compact-density surfaces, and I-9 requires every staff decision to be
 *     auditable. Section 7 has no row for it.
 *
 * Both are marked `absentFromSpecScreenTable` rather than quietly folded in.
 */

import type { Component } from 'vue'

import type { IconName } from '@/components/base/icons'
import type { Role } from '@/lib/auth/contract'
import { ADMIN_GRID_MIN_WIDTH } from '@/lib/viewport'

import type { ScreenId } from './interaction-states'

export type ScreenAccess =
  /** Reachable signed in or out. Legal and the landing page. */
  | { kind: 'anonymous' }
  /** Only when signed out. Signing in redirects away, role-aware. */
  | { kind: 'anonymous-only' }
  /** Signed in, and the role must be in the list. */
  | { kind: 'role'; roles: readonly Role[] }

export interface NavPlacement {
  label: string
  icon: IconName
  /** Sort order within the role's navigation. */
  order: number
  /**
   * Shown in the 360px bottom tab bar. DESIGN.md section 12 caps it at four, and
   * the fourth slot is "More", so at most three screens per role set this.
   */
  primary: boolean
  /** Groups the post-launch admin console apart from the day-to-day staff surfaces. */
  group?: 'admin-console'
}

export interface ScreenDefinition {
  id: ScreenId
  /** Route name. Kept equal to the id so a failed navigation names the screen. */
  name: ScreenId
  path: string
  title: string
  /** The spec section 7 row this implements, verbatim, or `null` for system routes. */
  specScreen: string | null
  workItems: readonly string[]
  access: ScreenAccess
  /**
   * DESIGN.md section 6. Which scale a screen uses is a documented decision, not
   * a per-component guess, so it is a field rather than a class somebody
   * remembers to add.
   */
  density: 'default' | 'compact'
  /**
   * The sign-in layout (`SignInShell`), rather than the one the access rule
   * implies. Only sign-in takes it: the 2026-09-26 design covers that screen and
   * no other, and the rest of the signed-out screens keep `PublicShell` until a
   * design says otherwise.
   */
  layout?: 'sign-in'
  /**
   * Below this width the screen refuses to render and offers a way out.
   * DESIGN.md section 12: `ADM-*` grids are "explicitly not supported below 768"
   * rather than silently broken.
   */
  minWidth?: number
  /** Not in ADR 0004's first slice. The route exists; the feature does not. */
  deferred?: { by: string; note: string }
  /** Documented divergence from spec section 7's table. */
  absentFromSpecScreenTable?: string
  /**
   * The screen this one was reached from, for the breadcrumb. Explicit rather
   * than derived from the URL: `/clients/new` and `/clients/:id` are both children
   * of the list, but `/referrals/inbox` is not a child of `/referrals/mine` even
   * though the paths nest.
   */
  parent?: ScreenId
  nav?: NavPlacement
  /**
   * The ten screens carrying a `deferred` field all point at
   * `system/DeferredScreen.vue`, which reads its content back out of this
   * catalog. Ten files that differed only by an import would be ten places to
   * forget something; when one of those features is built it gets its own
   * component and this entry changes.
   */
  component: () => Promise<Component>
}

// Casts on the lazy imports: `defineAsyncComponent`-style loaders are typed as
// returning the module, and Vue Router accepts that, but `Component` is the
// honest annotation for what the catalog promises.
const load = (loader: () => Promise<unknown>): (() => Promise<Component>) =>
  loader as () => Promise<Component>

export const SCREENS: readonly ScreenDefinition[] = [
  // ── Public ───────────────────────────────────────────────────────────────
  {
    id: 'landing',
    name: 'landing',
    path: '/',
    title: 'Match Point',
    specScreen: 'Landing / splash',
    workItems: ['ACC-01'],
    access: { kind: 'anonymous' },
    density: 'default',
    component: load(() => import('./public/LandingScreen.vue'))
  },
  {
    id: 'login',
    name: 'login',
    path: '/login',
    title: 'Sign in',
    specScreen: 'Login',
    workItems: ['ACC-03'],
    access: { kind: 'anonymous-only' },
    density: 'default',
    layout: 'sign-in',
    component: load(() => import('./public/LoginScreen.vue'))
  },
  {
    id: 'signup',
    name: 'signup',
    path: '/signup',
    title: 'Create an account',
    specScreen: 'Create account',
    workItems: ['ACC-02', 'ACC-05'],
    access: { kind: 'anonymous-only' },
    density: 'default',
    component: load(() => import('./public/SignUpScreen.vue'))
  },
  {
    id: 'confirm-signup',
    name: 'confirm-signup',
    path: '/confirm',
    title: 'Confirm your account',
    specScreen: null,
    workItems: ['ACC-02'],
    // anonymous-only, like sign-up itself: confirming is something you do
    // before you have a session, and the account it confirms cannot sign in yet.
    access: { kind: 'anonymous-only' },
    density: 'default',
    absentFromSpecScreenTable:
      'Spec section 7 rolls confirmation into Create account, and the interaction-state registry in this directory already required the screen: the signup success note reads "route to the confirmation notice naming the address the email went to". With no row there was no screen, so an account could be created and never confirmed. Verified against deployed dev on 2026-09-10.',
    component: load(() => import('./public/ConfirmSignUpScreen.vue'))
  },
  {
    id: 'auth-callback',
    name: 'auth-callback',
    path: '/auth/callback',
    title: 'Signing you in',
    specScreen: null,
    workItems: ['ACC-06'],
    // Anonymous, not anonymous-only: the browser arrives here from the provider
    // before any session exists, and a stale session in another tab must not
    // bounce the callback away before it has read its code.
    access: { kind: 'anonymous' },
    density: 'default',
    absentFromSpecScreenTable:
      'ADR 0012 adds sign-in with Google, Apple and Microsoft, which the spec predates. The provider returns the browser here with a one-time code; this route exchanges it and sends the person on to their home or, for somebody new, to finish their account. It is the redirect URI registered on the Cognito app client.',
    component: load(() => import('./public/AuthCallbackScreen.vue'))
  },
  {
    id: 'finish-account',
    name: 'finish-account',
    path: '/finish-account',
    title: 'Finish your account',
    specScreen: null,
    workItems: ['ACC-06', 'ACC-05'],
    access: { kind: 'anonymous-only' },
    density: 'default',
    absentFromSpecScreenTable:
      'ADR 0012 decision 4: somebody who signs in with Google has an identity and no account. The provider does not know their organization or what they are called in the product, so this asks for those two things and the terms, then calls POST /auth/federated-signup. It is ACC-02 for a person who arrived without a password.',
    component: load(() => import('./public/FinishAccountScreen.vue'))
  },
  {
    id: 'forgot-password',
    name: 'forgot-password',
    path: '/forgot-password',
    title: 'Reset your password',
    specScreen: 'Forgot password',
    workItems: ['ACC-04'],
    access: { kind: 'anonymous-only' },
    density: 'default',
    component: load(() => import('./public/ForgotPasswordScreen.vue'))
  },
  {
    id: 'legal',
    name: 'legal',
    path: '/legal',
    title: 'Terms and privacy',
    specScreen: 'Legal',
    workItems: ['ACC-05'],
    access: { kind: 'anonymous' },
    density: 'default',
    component: load(() => import('./public/LegalScreen.vue'))
  },

  // ── All roles ────────────────────────────────────────────────────────────
  {
    id: 'home',
    name: 'home',
    path: '/home',
    title: 'Home',
    specScreen: 'Home dashboard',
    workItems: ['CLI-05'],
    access: { kind: 'role', roles: ['provider_admin', 'mp_admin'] },
    density: 'default',
    nav: { label: 'Home', icon: 'home', order: 0, primary: true },
    component: load(() => import('./HomeScreen.vue'))
  },

  {
    id: 'directory',
    name: 'directory',
    path: '/directory',
    // "Search" on screen, `directory` as the id and the path. The label is what
    // a clinician does here; the id is what the thing is, and it is the key the
    // interaction-state registry, the breadcrumbs and two child screens all
    // join on. Renaming the route as well would break every link a provider
    // has already sent, and buy nothing a reader would notice.
    title: 'Search',
    specScreen: null,
    workItems: ['DIR-01'],
    // Gated behind login. The directory is the product and ADR 0007 makes it the
    // first release, but a signed-out crawl of every facility's admissions line
    // is not what a provider agreed to when they filled the form in.
    access: { kind: 'role', roles: ['provider_admin'] },
    density: 'compact',
    absentFromSpecScreenTable:
      'Section 7 predates ADR 0007 and has a Search results row, which is the scored search that feeds the referral loop. The directory is a different surface with a different promise: filters a clinician sets themselves, no ranking, and every row readable without sending anything. A provider reaches it too, because the only way to check how your own listing reads is to read it where a clinician does.',
    nav: { label: 'Search', icon: 'search', order: 1, primary: true },
    component: load(() => import('./directory/DirectoryScreen.vue'))
  },
  {
    id: 'directory-company',
    name: 'directory-company',
    path: '/companies/:orgId',
    title: 'Company',
    specScreen: null,
    workItems: ['DIR-02'],
    access: { kind: 'role', roles: ['provider_admin'] },
    density: 'default',
    parent: 'directory',
    absentFromSpecScreenTable:
      'ADR 0007 decision 4 reduces the organization to a light page tying its listings together, which section 7 has no row for because the organization was itself the listed entity when that table was written. Deliberately not a second rich profile: the org record still carries criteria and a bed count, and neither appears here, because a clinician cannot act on them at company grain when it is the facility that accepts a referral.',
    component: load(() => import('./directory/CompanyScreen.vue'))
  },
  {
    id: 'directory-program',
    name: 'directory-program',
    path: '/programs/:programId',
    title: 'Program listing',
    specScreen: null,
    workItems: ['DIR-03'],
    access: { kind: 'role', roles: ['provider_admin'] },
    density: 'default',
    parent: 'directory',
    absentFromSpecScreenTable:
      'The listing a clinician opens from the directory. Section 7 has a Facility profile row written when the organization was the listed entity; ADR 0007 makes the program the listed entity, so this renders one program through the form that produced it rather than one company through a fixed profile.',
    component: load(() => import('./directory/DirectoryProgramScreen.vue'))
  },

  // ── Customer accounts ────────────────────────────────────────────────────
  {
    id: 'org-profile',
    name: 'org-profile',
    path: '/org/profile',
    title: 'Company page',
    specScreen: 'Org profile edit',
    // ORG-03 (criteria) and ORG-04 (price range) left this screen with ADR 0014:
    // both are program facts now, and the company page is general information.
    workItems: ['ORG-02'],
    access: { kind: 'role', roles: ['provider_admin'] },
    density: 'default',
    // Demoted from primary by ADR 0007 decision 4: the organization profile
    // is now a light company page and Programs is the surface a provider
    // actually works in. The 360 bar holds three primary tabs plus More,
    // so one of the two had to give and this is the one the ADR reduced.
    nav: { label: 'Company page', icon: 'org', order: 3, primary: false },
    component: load(() => import('./org/OrgProfileScreen.vue'))
  },
  {
    id: 'org-programs',
    name: 'org-programs',
    path: '/org/programs',
    title: 'Your programs',
    specScreen: null,
    workItems: ['PRG-06'],
    access: { kind: 'role', roles: ['provider_admin'] },
    density: 'default',
    absentFromSpecScreenTable:
      'Section 7 predates ADR 0007 and has one Org profile row, because the organization was the listed entity when it was written. A company runs as many programs as it has facilities, so the provider lands on a list rather than a profile and "my profile" stops being a thing after the second building.',
    nav: { label: 'Programs', icon: 'programs', order: 2, primary: true },
    component: load(() => import('./org/ProgramListScreen.vue'))
  },
  {
    id: 'org-program-form',
    name: 'org-program-form',
    path: '/org/programs/:programId',
    title: 'Program profile',
    specScreen: null,
    workItems: ['PRG-07', 'PRG-08'],
    access: { kind: 'role', roles: ['provider_admin'] },
    density: 'default',
    parent: 'org-programs',
    absentFromSpecScreenTable:
      'The onboarding form itself, which section 7 has no row for because it did not exist when that table was written. It renders whatever GET /programs/forms/{source} returns, so a sixth form is a seed rather than a screen.',
    component: load(() => import('./org/ProgramFormScreen.vue'))
  },
  {
    id: 'org-approval',
    name: 'org-approval',
    path: '/org/approval',
    title: 'Approval status',
    specScreen: null,
    workItems: ['ORG-05'],
    access: { kind: 'role', roles: ['provider_admin'] },
    density: 'default',
    absentFromSpecScreenTable:
      'DESIGN.md section 11 designs this screen in full and calls it the first thing the first customer sees; spec section 7 has no row for it.',
    nav: { label: 'Approval status', icon: 'approvals', order: 3, primary: false },
    component: load(() => import('./org/OrgApprovalScreen.vue'))
  },
  {
    id: 'org-analytics',
    name: 'org-analytics',
    path: '/org/analytics',
    title: 'Analytics',
    specScreen: 'Analytics',
    workItems: ['ANL-02'],
    access: { kind: 'role', roles: ['provider_admin'] },
    density: 'compact',
    deferred: { by: 'ADR 0004 section 7', note: 'All of ANL is out of the first slice, including the rollup pipeline it reads.' },
    // No `nav`: taken out of the navigation until ANL is built. A menu entry
    // that opens a "not yet" page is a promise the product is not making. The
    // route stays so spec section 7's row still resolves.
    component: load(() => import('./system/DeferredScreen.vue'))
  },
  {
    id: 'subscription',
    name: 'subscription',
    path: '/org/subscription',
    title: 'Subscription',
    specScreen: 'Subscription',
    workItems: ['BILL-02'],
    access: { kind: 'role', roles: ['provider_admin'] },
    density: 'default',
    deferred: {
      by: 'ADR 0004 section 7',
      note: 'All of BILL is out of the first slice, and spec section 12 records the two price points as an unresolved founder decision.'
    },
    // No `nav`, for the same reason as Analytics: out of the navigation until
    // BILL exists.
    component: load(() => import('./system/DeferredScreen.vue'))
  },

  // ── Match Point staff ────────────────────────────────────────────────────
  {
    id: 'staff-approvals',
    name: 'staff-approvals',
    path: '/staff/approvals',
    // "Approvals", not "Account approvals": the screen carries two queues, and
    // naming it after one of them is how the other went unnoticed.
    title: 'Approvals',
    specScreen: 'Staff approvals',
    workItems: ['ORG-06', 'ORG-07', 'PRG-10'],
    access: { kind: 'role', roles: ['mp_admin'] },
    density: 'compact',
    nav: { label: 'Approvals', icon: 'approvals', order: 1, primary: true },
    component: load(() => import('./staff/StaffApprovalsScreen.vue'))
  },
  {
    id: 'staff-form-options',
    name: 'staff-form-options',
    path: '/staff/form-options',
    title: 'Form options',
    specScreen: null,
    workItems: ['ADM-08'],
    access: { kind: 'role', roles: ['mp_admin'] },
    density: 'compact',
    minWidth: ADMIN_GRID_MIN_WIDTH,
    absentFromSpecScreenTable:
      'ADR 0015 lets staff edit the choices the program wizard offers. Spec section 7 predates forms-as-data, so it has no screen for editing them.',
    nav: { label: 'Form options', icon: 'layers', order: 2, primary: false },
    component: load(() => import('./staff/FormOptionsScreen.vue'))
  },
  {
    id: 'staff-notification-settings',
    name: 'staff-notification-settings',
    // /settings, not /staff: the staff emails' footer links here, and a
    // customer's notification settings will want the same address one day.
    path: '/settings/notifications',
    title: 'Notifications',
    specScreen: null,
    workItems: ['NTF-04'],
    access: { kind: 'role', roles: ['mp_admin'] },
    density: 'compact',
    absentFromSpecScreenTable:
      'ADR 0017 emails staff when approval work arrives and lets each of them choose which queues do. Spec section 7 predates staff notifications, so it has no settings screen.',
    nav: { label: 'Notifications', icon: 'mail', order: 3, primary: false },
    component: load(() => import('./staff/NotificationSettingsScreen.vue'))
  },
  {
    id: 'staff-flag-review',
    name: 'staff-flag-review',
    path: '/staff/flags',
    title: 'Sentinel flag review',
    specScreen: 'Staff flag review',
    workItems: ['ORG-08'],
    access: { kind: 'role', roles: ['mp_admin'] },
    density: 'compact',
    deferred: {
      by: 'ADR 0004 section 4',
      note: 'The referral payload carries no free text, so there is no Sentinel and no violation counter to review.'
    },
    // No `nav`, for the same reason as Analytics: out of the navigation until
    // its work item is built.
    component: load(() => import('./system/DeferredScreen.vue'))
  },
  {
    id: 'staff-audit',
    name: 'staff-audit',
    path: '/staff/audit',
    title: 'Audit log',
    specScreen: null,
    workItems: ['ORG-09'],
    access: { kind: 'role', roles: ['mp_admin'] },
    density: 'compact',
    deferred: { by: 'ADR 0004 section 7', note: 'ORG-09 is out of the first slice; the hash-chained log it reads already exists in the schema.' },
    absentFromSpecScreenTable:
      'Spec section 6 exposes GET /admin/audit and DESIGN.md section 6 lists the audit log view as a compact surface, but spec section 7 has no row for it.',
    // No `nav`, for the same reason as Analytics: out of the navigation until
    // its work item is built.
    component: load(() => import('./system/DeferredScreen.vue'))
  },

  // ── Post-launch admin console. ADM-*: unsupported below 768. ─────────────
  // None of these is in the navigation. The routes resolve so spec section 7's
  // rows still do, and each one gets a `nav` back, with `group: 'admin-console'`,
  // when its feature is built.
  {
    id: 'admin-cross-org-analytics',
    name: 'admin-cross-org-analytics',
    path: '/admin/analytics',
    title: 'Cross-org analytics',
    specScreen: 'Cross-org analytics',
    workItems: ['ADM-01'],
    access: { kind: 'role', roles: ['mp_admin'] },
    density: 'compact',
    minWidth: ADMIN_GRID_MIN_WIDTH,
    deferred: {
      by: 'ADR 0004 section 7',
      note: 'The whole ADM console is post-launch. It also has no users to serve yet — there is no staff console until there are organizations to administer.'
    },
    // No `nav`, for the same reason as Analytics: out of the navigation until
    // its work item is built.
    component: load(() => import('./system/DeferredScreen.vue'))
  },
  {
    id: 'admin-platform-overview',
    name: 'admin-platform-overview',
    path: '/admin/overview',
    title: 'Platform overview',
    specScreen: 'Platform overview',
    workItems: ['ADM-02'],
    access: { kind: 'role', roles: ['mp_admin'] },
    density: 'compact',
    minWidth: ADMIN_GRID_MIN_WIDTH,
    deferred: {
      by: 'ADR 0004 section 7',
      note: 'The whole ADM console is post-launch. It also has no users to serve yet — there is no staff console until there are organizations to administer.'
    },
    // No `nav`, for the same reason as Analytics: out of the navigation until
    // its work item is built.
    component: load(() => import('./system/DeferredScreen.vue'))
  },
  {
    id: 'admin-user-management',
    name: 'admin-user-management',
    path: '/admin/users',
    title: 'User management',
    specScreen: 'User management',
    workItems: ['ADM-03'],
    access: { kind: 'role', roles: ['mp_admin'] },
    density: 'compact',
    minWidth: ADMIN_GRID_MIN_WIDTH,
    deferred: {
      by: 'ADR 0004 section 7',
      note: 'The whole ADM console is post-launch. It also has no users to serve yet — there is no staff console until there are organizations to administer.'
    },
    // No `nav`, for the same reason as Analytics: out of the navigation until
    // its work item is built.
    component: load(() => import('./system/DeferredScreen.vue'))
  },
  {
    id: 'admin-console',
    name: 'admin-console',
    path: '/admin/console',
    title: 'Runtime configuration',
    specScreen: 'Admin console',
    workItems: ['ADM-06'],
    access: { kind: 'role', roles: ['mp_admin'] },
    density: 'compact',
    minWidth: ADMIN_GRID_MIN_WIDTH,
    deferred: {
      by: 'ADR 0004 section 7',
      note: 'Engineering flags stay environment variables until a staff user exists to operate a config subsystem.'
    },
    // No `nav`, for the same reason as Analytics: out of the navigation until
    // its work item is built.
    component: load(() => import('./system/DeferredScreen.vue'))
  },
  {
    id: 'admin-moderation-queue',
    name: 'admin-moderation-queue',
    path: '/admin/moderation',
    title: 'Moderation queue',
    specScreen: 'Moderation queue',
    workItems: ['ADM-07'],
    access: { kind: 'role', roles: ['mp_admin'] },
    density: 'compact',
    minWidth: ADMIN_GRID_MIN_WIDTH,
    deferred: { by: 'ADR 0004 section 7', note: 'The whole ADM console is post-launch, and the Sentinel flags it unifies are deferred too.' },
    // No `nav`, for the same reason as Analytics: out of the navigation until
    // its work item is built.
    component: load(() => import('./system/DeferredScreen.vue'))
  },

  // ── System ───────────────────────────────────────────────────────────────
  {
    id: 'forbidden',
    name: 'forbidden',
    path: '/forbidden',
    title: 'Not available to your role',
    specScreen: null,
    workItems: ['FND-11'],
    access: { kind: 'anonymous' },
    density: 'default',
    component: load(() => import('./system/ForbiddenScreen.vue'))
  },
  {
    id: 'not-found',
    name: 'not-found',
    // FND-06 serves index.html for any unmatched path, so this is what a bad URL
    // resolves to rather than a CloudFront 404.
    path: '/:pathMatch(.*)*',
    title: 'That page does not exist',
    specScreen: null,
    workItems: ['FND-11'],
    access: { kind: 'anonymous' },
    density: 'default',
    component: load(() => import('./system/NotFoundScreen.vue'))
  }
]

export const SCREENS_BY_ID: Readonly<Record<ScreenId, ScreenDefinition>> = Object.fromEntries(
  SCREENS.map(screen => [screen.id, screen])
) as Record<ScreenId, ScreenDefinition>

/** Screens that implement a row of spec section 7's table. */
export const SPEC_SCREENS = SCREENS.filter(s => s.specScreen !== null)

/** Navigation for one role, ordered. Never derives permission — see below. */
export function navigationFor (role: Role): readonly ScreenDefinition[] {
  return SCREENS.filter(
    screen => screen.nav !== undefined && screen.access.kind === 'role' && screen.access.roles.includes(role)
  ).sort((a, b) => (a.nav?.order ?? 0) - (b.nav?.order ?? 0))
}

/**
 * Whether a role may route to a screen.
 *
 * This decides **what to show**, never what the user may do. Per I-3 and
 * `apps/README.md`, a UI that hides a field is not security; the server decides
 * what to return. Deleting this function would make the app ugly, not insecure.
 */
export function roleMayView (screen: ScreenDefinition, role: Role | null): boolean {
  if (screen.access.kind !== 'role') return true
  return role !== null && screen.access.roles.includes(role)
}
